'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import NextImage from 'next/image';
import type {ReactNode} from 'react';
import {useAfterFirstPaint} from '../../../hooks';
import {Button} from '../../primitives';
import {isSignedMediaUrl} from './isSignedMediaUrl';
import type {ListingCardAction, ListingCardAspectRatio} from './types';

const DEFAULT_MEDIA_RATIO: ListingCardAspectRatio = {mobile: '353 / 471', desktop: '348 / 360'};
// From md up, cards sit three to a row inside the 1160 px container, so they never exceed ~365 px.
const MEDIA_SIZES = '(max-width: 599px) 100vw, (max-width: 899px) 50vw, 380px';

export interface ListingCardMediaProps {
  image: string;
  aspectRatio?: ListingCardAspectRatio;
  fixedMedia?: boolean;
  deferImage?: boolean;
  tags?: ReactNode;
  hoverActions?: ListingCardAction[];
}

function toSize(ratio: string) {
  const [width, height] = ratio.split('/').map(part => Number(part.trim()));
  return {width, height};
}

const Media = styled(Box, {shouldForwardProp: propName => propName !== '$desktop' && propName !== '$fixed'})<{
  $desktop: string;
  $fixed?: boolean;
}>(({theme, $desktop, $fixed}) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: theme.palette.colors.surface.neutral.disabled,
  // The media has only absolutely-positioned children, so it has no intrinsic
  // height to resist flex-shrink; pin it so the size always holds.
  flexShrink: 0,
  // Mobile: fixed 300×335 image (the card is 300px wide).
  height: 335,
  // Desktop: size by aspect ratio (or fixed px) instead.
  [theme.breakpoints.up('md')]: {
    ...($fixed ? toSize($desktop) : {height: 'auto', aspectRatio: $desktop}),
  },
}));

const TagRow = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: theme.spacing(2),
  insetInlineEnd: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(0.5),
}));

// Hover overlay over the image: a 70% scrim plus the Register-Interest / View
// button pair, revealed on hover. The scrim itself stays pointer-events: none
// so clicks fall through to the card link underneath; only the buttons opt
// back into pointer-events via data-card-interactive. Only rendered for cards
// that have actions — action-less cards (news) get no overlay and so have no
// hover state at all.
const HoverActions = styled(Box)(({theme}) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'grid',
  justifyContent: 'center',
  alignContent: 'center',
  justifyItems: 'stretch',
  gap: theme.spacing(1.5),
  background: theme.palette.colors.surface.darkOverlayStrong,
  opacity: 0,
  pointerEvents: 'none',
  transition: theme.transitions.create('opacity', {duration: 500}),
}));

export function ListingCardMedia({
  image,
  aspectRatio = DEFAULT_MEDIA_RATIO,
  fixedMedia,
  deferImage = false,
  tags,
  hoverActions,
}: ListingCardMediaProps) {
  const showImage = useAfterFirstPaint(deferImage);
  // Only the desktop size is used now; mobile cards fill a fixed-height card.
  const desktop = typeof aspectRatio === 'string' ? aspectRatio : aspectRatio.desktop;
  return (
    <Media $desktop={desktop} $fixed={fixedMedia}>
      {image && showImage && (
        <NextImage
          data-card-media
          src={image}
          alt=""
          fill
          sizes={MEDIA_SIZES}
          style={{objectFit: 'cover'}}
          // CRM media URLs are re-signed per request (Expires/Signature/Key-Pair-Id)
          // — passing them through the optimizer would churn its cache on every load.
          unoptimized={isSignedMediaUrl(image)}
        />
      )}
      {tags && <TagRow>{tags}</TagRow>}
      {hoverActions?.length ? (
        <HoverActions data-card-actions>
          {hoverActions.map((action, index) => (
            <Button
              key={action.label}
              data-card-interactive
              variant={index === 0 ? 'contained-dark' : 'contained-light'}
              onClick={action.onClick}
              // Figma: View = white fill + 1px tan border + tan text (RI is the filled-tan primary).
              sx={index === 0 ? undefined : {border: '1px solid', borderColor: theme => theme.palette.colors.border.primary.default}}
            >
              {action.label}
            </Button>
          ))}
        </HoverActions>
      ) : null}
    </Media>
  );
}

ListingCardMedia.displayName = 'ListingCardMedia';
